import { X } from 'lucide-react';
import { useState } from 'react';
import RecipientPicker from './RecipientPicker';
import { isMail } from './scheduleSlots';

// What separates two addresses typed in a row.
const SEPARATORS = [',', ';', ' '];

/**
 * A line of addresses (« À », « Cc », « Cci »): a chip per recipient, then
 * what is being typed. Enter, a comma or leaving the field makes a valid
 * address a chip; the record's addresses are offered as one types, and all of
 * them in RecipientPicker. `recipients`: [{ name, address }]; `contacts`:
 * [{ name, email, tag }]; `elsewhere`: { address: where it is already }, an
 * address of another line, never offered here; `text`: what is typed, kept by
 * the window so that « Envoyer » takes it too.
 */
const RecipientField = ({ label, recipients, onChange, text, onText, contacts = [], elsewhere = {}, pickerTexts, autoFocus, children }) => {
  const [focused, setFocused] = useState(false);
  const query = text.trim().toLowerCase();
  const taken = new Set(recipients.map((one) => one.address.toLowerCase()));
  const offered = focused
    ? contacts.filter(
        (contact) =>
          contact.email &&
          !taken.has(contact.email.toLowerCase()) &&
          !elsewhere[contact.email.toLowerCase()] &&
          (!query || contact.name?.toLowerCase().includes(query) || contact.email.toLowerCase().includes(query)),
      )
    : [];
  const add = (recipient) => {
    if (!taken.has(recipient.address.toLowerCase())) onChange([...recipients, recipient]);
    onText('');
  };
  const commit = () => {
    if (isMail(text)) add({ name: '', address: text.trim() });
  };
  const invalid = Boolean(query) && !focused && !isMail(text);

  return (
    <div className="relative flex min-h-[44px] items-center gap-2 border-b border-gray-100 px-4 py-1.5">
      <span className="shrink-0 text-sm text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {recipients.map((recipient) => (
          <span
            key={recipient.address}
            title={recipient.address}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-2.5 pr-1 text-sm text-gray-900"
          >
            <span className="truncate">{recipient.name || recipient.address}</span>
            <button
              type="button"
              aria-label={`${label} ${recipient.address}`}
              onClick={() => onChange(recipients.filter((one) => one !== recipient))}
              className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </span>
        ))}
        <input
          type="text"
          aria-label={label}
          autoFocus={autoFocus}
          value={text}
          onChange={(event) => {
            const value = event.target.value;
            const last = value.slice(-1);
            if (SEPARATORS.includes(last) && isMail(value.slice(0, -1))) add({ name: '', address: value.slice(0, -1).trim() });
            else onText(value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && text.trim()) {
              event.preventDefault();
              if (offered.length && !isMail(text)) add({ name: offered[0].name, address: offered[0].email });
              else commit();
            } else if (event.key === 'Backspace' && !text && recipients.length) {
              onChange(recipients.slice(0, -1));
            }
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          className={`min-w-[120px] flex-1 border-0 bg-transparent p-0 py-1 text-sm focus:outline-none focus:ring-0 ${
            invalid ? 'text-red-600' : 'text-gray-900'
          }`}
        />
      </div>
      {children}
      {pickerTexts && contacts.some((contact) => contact.email) && (
        <RecipientPicker
          label={label}
          options={contacts.filter((contact) => contact.email)}
          recipients={recipients}
          elsewhere={elsewhere}
          onChange={onChange}
          texts={pickerTexts}
        />
      )}
      {offered.length > 0 && (
        <ul className="absolute left-12 top-full z-10 mt-1 max-h-56 w-80 overflow-y-auto rounded-lg border border-gray-100 bg-white p-1 shadow-lg motion-safe:animate-menuIn">
          {offered.map((contact) => (
            <li key={contact.email}>
              <button
                type="button"
                // Before the field's blur, which would close the list.
                onMouseDown={(event) => {
                  event.preventDefault();
                  add({ name: contact.name ?? '', address: contact.email });
                }}
                className="flex w-full flex-col rounded-md px-3 py-2 text-left hover:bg-gray-100"
              >
                <span className="text-sm text-gray-900">{contact.name}</span>
                <span className="text-xs text-gray-500">{contact.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecipientField;
